import { useMemo, useState } from 'react';
import { logger } from '../../../shared/utils/logger.js';
import type { AssignmentRow } from '../api/employees.api';
import {
  useAssignEmployeeToService,
  useAssignServiceToEmployee,
  useEmployeeAssignments,
  useEmployees,
  useRemoveAssignment,
  useServiceAssignments,
  useServicePicker,
} from '../queries';

type AssignmentManagerModalProps =
  | {
      open: boolean;
      mode: 'by-employee';
      employeeUuid: string;
      employeeName: string;
      onClose: () => void;
      onToast?: (toast: { type: 'success' | 'error'; message: string }) => void;
    }
  | {
      open: boolean;
      mode: 'by-service';
      serviceUuid: string;
      serviceName: string;
      onClose: () => void;
      onToast?: (toast: { type: 'success' | 'error'; message: string }) => void;
    };

const MODULE = 'employees-roles-assignment-manager';

const logEvent = (event: string, fields: Record<string, unknown> = {}): void => {
  logger.info(MODULE, event, { event, ...fields });
};

const PRICE_PATTERN = /^(\d+)(\.\d{1,2})?$/;

const errorText = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

const AssignmentManagerModal = (props: AssignmentManagerModalProps) => {
  const { open, mode, onClose, onToast } = props;

  // Identifiers for both axes — at least one is constant per mode.
  const fixedEmployeeUuid = mode === 'by-employee' ? props.employeeUuid : null;
  const fixedServiceUuid = mode === 'by-service' ? props.serviceUuid : null;
  const fixedLabel = mode === 'by-employee' ? props.employeeName : props.serviceName;

  const employeeAssignments = useEmployeeAssignments(
    open && mode === 'by-employee' ? fixedEmployeeUuid : null,
  );
  const serviceAssignments = useServiceAssignments(
    open && mode === 'by-service' ? fixedServiceUuid : null,
  );

  const employeesQuery = useEmployees(open && mode === 'by-service');
  const servicePicker = useServicePicker(open && mode === 'by-employee');

  const assignServiceMutation = useAssignServiceToEmployee(fixedEmployeeUuid || '');
  const assignEmployeeMutation = useAssignEmployeeToService(fixedServiceUuid || '');
  const removeMutation = useRemoveAssignment();

  const [pickedUuid, setPickedUuid] = useState<string>('');
  const [priceOverride, setPriceOverride] = useState<string>('');
  const [isPrimary, setIsPrimary] = useState<boolean>(false);
  const [visibility, setVisibility] = useState<'PUBLIC' | 'HIDDEN'>('PUBLIC');
  const [localError, setLocalError] = useState<string>('');

  const rows: AssignmentRow[] = useMemo(
    () => (
      mode === 'by-employee'
        ? employeeAssignments.data || []
        : serviceAssignments.data || []
    ),
    [mode, employeeAssignments.data, serviceAssignments.data],
  );
  const loading =
    mode === 'by-employee' ? employeeAssignments.isLoading : serviceAssignments.isLoading;
  const loadError =
    mode === 'by-employee' ? employeeAssignments.error : serviceAssignments.error;

  // ----- picker candidates -----
  const assignedOtherAxisUuids = useMemo(
    () =>
      new Set(rows.map((r) => (mode === 'by-employee' ? r.serviceUuid : r.employeeUuid))),
    [rows, mode],
  );

  const candidates = useMemo(() => {
    if (mode === 'by-employee') {
      return (servicePicker.data || []).filter(
        (s) => !assignedOtherAxisUuids.has(s.serviceUuid),
      );
    }
    return (employeesQuery.data || []).filter(
      (e) => e.status === 'ACTIVE' && !assignedOtherAxisUuids.has(e.employeeUuid),
    );
  }, [mode, servicePicker.data, employeesQuery.data, assignedOtherAxisUuids]);

  const candidatesLoading =
    mode === 'by-employee' ? servicePicker.isLoading : employeesQuery.isLoading;

  if (!open) return null;

  const resetForm = () => {
    setPickedUuid('');
    setPriceOverride('');
    setIsPrimary(false);
    setVisibility('PUBLIC');
    setLocalError('');
  };

  const handleClose = () => {
    logEvent('assignment_manager_close', {
      mode,
      employeeUuid: fixedEmployeeUuid,
      serviceUuid: fixedServiceUuid,
    });
    resetForm();
    onClose();
  };

  const handleAdd = () => {
    if (!pickedUuid) {
      setLocalError(mode === 'by-employee' ? 'Pick a service.' : 'Pick an employee.');
      return;
    }
    if (priceOverride && !PRICE_PATTERN.test(priceOverride)) {
      setLocalError('Price override must be a number with up to two decimals.');
      return;
    }
    setLocalError('');
    const writeInput = {
      priceOverride: priceOverride || null,
      isPrimary,
      visibility,
    };

    const onSuccess = () => {
      onToast?.({ type: 'success', message: 'Assignment created.' });
      resetForm();
    };
    const onError = (err: unknown) =>
      onToast?.({ type: 'error', message: errorText(err, 'Could not create assignment.') });

    logEvent('assignment_manager_submit', {
      mode,
      employeeUuid: mode === 'by-employee' ? fixedEmployeeUuid : pickedUuid,
      serviceUuid: mode === 'by-service' ? fixedServiceUuid : pickedUuid,
      isPrimary,
    });

    if (mode === 'by-employee') {
      assignServiceMutation.mutate(
        { serviceUuid: pickedUuid, input: writeInput },
        { onSuccess, onError },
      );
    } else {
      assignEmployeeMutation.mutate(
        { employeeUuid: pickedUuid, input: writeInput },
        { onSuccess, onError },
      );
    }
  };

  const handleRemove = (row: AssignmentRow) => {
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm('Remove this assignment?');
      if (!confirmed) {
        logEvent('assignment_manager_remove_cancelled', { assignmentUuid: row.assignmentUuid });
        return;
      }
    }
    logEvent('assignment_manager_remove_confirmed', {
      assignmentUuid: row.assignmentUuid,
      employeeUuid: row.employeeUuid,
      serviceUuid: row.serviceUuid,
    });
    removeMutation.mutate(
      { employeeUuid: row.employeeUuid, serviceUuid: row.serviceUuid },
      {
        onSuccess: () => onToast?.({ type: 'success', message: 'Assignment removed.' }),
        onError: (e) =>
          onToast?.({ type: 'error', message: errorText(e, 'Could not remove assignment.') }),
      },
    );
  };

  const otherAxisName = (row: AssignmentRow): string => {
    if (mode === 'by-employee') {
      const found = (servicePicker.data || []).find((s) => s.serviceUuid === row.serviceUuid);
      return found?.serviceName || row.serviceUuid;
    }
    const found = (employeesQuery.data || []).find((e) => e.employeeUuid === row.employeeUuid);
    return found?.name || row.employeeUuid;
  };

  const submitting = assignServiceMutation.isPending || assignEmployeeMutation.isPending;
  const submitError = errorText(
    mode === 'by-employee' ? assignServiceMutation.error : assignEmployeeMutation.error,
    '',
  );

  return (
    <div className="role-editor-overlay" role="dialog" aria-modal="true" aria-label="Manage assignments">
      <div className="schedule-editor">
        <header className="schedule-editor__header">
          <div>
            <h3 className="schedule-editor__title">
              {mode === 'by-employee' ? 'Services assigned to' : 'Staff assigned to'} — {fixedLabel}
            </h3>
            <p className="schedule-editor__subtitle">
              {mode === 'by-employee'
                ? 'Pick the services this employee can deliver. Override price per assignment if needed.'
                : 'Pick the employees that can deliver this service. Override price per assignment if needed.'}
            </p>
          </div>
          <button type="button" className="schedule-editor__close" onClick={handleClose}>
            Close
          </button>
        </header>

        {loading && <div className="roles-tab__loading">Loading assignments…</div>}

        {!loading && loadError && (
          <div className="roles-tab__error" role="alert">
            {errorText(loadError, 'Could not load assignments.')}
          </div>
        )}

        {!loading && !loadError && rows.length === 0 && (
          <p className="schedule-editor__notice">No assignments yet.</p>
        )}

        {!loading && !loadError && rows.length > 0 && (
          <table className="roles-tab__table" aria-label="Current assignments">
            <thead>
              <tr>
                <th>{mode === 'by-employee' ? 'Service' : 'Employee'}</th>
                <th>Price override</th>
                <th>Primary</th>
                <th>Visibility</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.assignmentUuid}>
                  <td><strong>{otherAxisName(row)}</strong></td>
                  <td>{row.priceOverride ?? <span style={{ opacity: 0.6 }}>—</span>}</td>
                  <td>
                    {row.isPrimary
                      ? <span className="roles-tab__status roles-tab__status--active">Primary</span>
                      : <span style={{ opacity: 0.6 }}>—</span>}
                  </td>
                  <td>{row.visibility}</td>
                  <td>
                    <div className="roles-tab__row-actions">
                      <button
                        type="button"
                        className="roles-tab__row-action roles-tab__row-action--danger"
                        onClick={() => handleRemove(row)}
                        disabled={removeMutation.isPending}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h4 className="schedule-editor__section-title">Add assignment</h4>

        {!candidatesLoading && candidates.length === 0 && mode === 'by-employee' ? (
          <p className="schedule-editor__notice">All services are assigned.</p>
        ) : null}

        <div className="schedule-editor__exception-form">
          <select
            className="roles-tab__filter"
            value={pickedUuid}
            onChange={(e) => setPickedUuid(e.target.value)}
            disabled={submitting || candidatesLoading}
            aria-label={mode === 'by-employee' ? 'Service' : 'Employee'}
            style={{ minWidth: 220 }}
          >
            <option value="">
              {candidatesLoading
                ? 'Loading…'
                : candidates.length === 0
                  ? mode === 'by-employee'
                    ? 'All services are assigned'
                    : 'All employees already assigned'
                  : mode === 'by-employee'
                    ? 'Pick a service'
                    : 'Pick an employee'}
            </option>
            {mode === 'by-employee'
              ? (servicePicker.data || [])
                  .filter((s) => !assignedOtherAxisUuids.has(s.serviceUuid))
                  .map((s) => (
                    <option key={s.serviceUuid} value={s.serviceUuid}>{s.serviceName}</option>
                  ))
              : (employeesQuery.data || [])
                  .filter((e) => !assignedOtherAxisUuids.has(e.employeeUuid))
                  .map((e) => (
                    <option key={e.employeeUuid} value={e.employeeUuid}>{e.name}</option>
                  ))}
          </select>

          <input
            type="text"
            inputMode="decimal"
            className="roles-tab__filter"
            placeholder="Price override (optional)"
            value={priceOverride}
            onChange={(e) => setPriceOverride(e.target.value)}
            disabled={submitting}
            style={{ minWidth: 160 }}
          />

          <select
            className="roles-tab__filter"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as 'PUBLIC' | 'HIDDEN')}
            disabled={submitting}
            aria-label="Visibility"
          >
            <option value="PUBLIC">Public</option>
            <option value="HIDDEN">Hidden</option>
          </select>

          <label className="schedule-editor__notice" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              disabled={submitting}
            />
            Primary
          </label>

          <button
            type="button"
            className="roles-tab__primary roles-tab__primary--compact"
            onClick={handleAdd}
            disabled={submitting || candidates.length === 0}
          >
            {submitting ? 'Saving…' : 'Add'}
          </button>
        </div>

        {(localError || submitError) && (
          <p className="role-editor__error" role="alert">{localError || submitError}</p>
        )}
      </div>
    </div>
  );
};

export default AssignmentManagerModal;
